import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import { TableColumn } from 'lp-components'
import { isEqual } from 'lodash'
import {
  Filter,
  PaginatedAndSortableTable,
  SearchInput,
  TableHeader,
  TableHeading,
  Tooltip,
  DownloadTableButton,
} from 'components'
import {
  convertToSearchParams,
  displayAttribute,
  displayEin,
  einSorter,
  formatUrl,
  tippyPlugins,
  useSearchParams,
  formatDate,
  dateSorter,
} from 'utils'
import AgreementStatusCell from './AgreementStatusCell'
import { TAXABLE_ACCOUNTS_INITIAL_INVESTMENT_TOOLTIP_CONTENT } from 'config/taxable-accounts'
import { PlanAgreementsFilterDialog } from '../components'
import { Link, useHistory } from 'react-router-dom'
import { Path, AccountAudience } from 'config/portal'
import {
  PlanAgreementsTableHeaders,
  ProductAgreementType,
} from 'config/products'
import { Icon as InfoIcon } from 'images/info.svg'

const { hideOnEsc, hideOnPopperBlur, preventEventBubbling } = tippyPlugins

const INITIAL_FILTERS = {
  agreementStatus: [],
  initialInvestment: [],
  planSponsor: [],
  startLastUpdatedDate: '',
  endLastUpdatedDate: '',
}

const propTypes = {
  servicesAgreements: PropTypes.arrayOf(Types.servicesAgreementSummary)
    .isRequired,
  noRowsFoundMessage: PropTypes.node,
  handleUploadClick: PropTypes.func.isRequired,
  tableId: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    agreementStatus: PropTypes.arrayOf(PropTypes.string),
    initialInvestment: PropTypes.arrayOf(PropTypes.string),
    planSponsor: PropTypes.arrayOf(PropTypes.string),
    startLastUpdatedDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    endLastUpdatedDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  currentUser: Types.user.isRequired,
  fieldMappings: PropTypes.arrayOf(PropTypes.object).isRequired,
  noServicesAgreements: PropTypes.bool.isRequired,
}

const defaultProps = {
  noRowsFoundMessage: null,
  filters: null,
}

function PlanAgreementDetailsLink({ data: { serviceAgreementID, name } }) {
  return (
    <td>
      <Link
        to={(location) => {
          return {
            pathname: formatUrl(
              `${Path.TAXABLE_ACCOUNTS_SERVICES_AGREEMENTS}/${serviceAgreementID}`
            ),
            state: { search: location.search },
          }
        }}
        className="link-text"
        id="plan-name-link"
      >
        {name}
      </Link>
    </td>
  )
}

PlanAgreementDetailsLink.propTypes = {
  data: PropTypes.shape({
    serviceAgreementID: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
}

function AgreementStatusCellWrapper({ data, handleUploadClick }) {
  return (
    <AgreementStatusCell data={data} handleUploadClick={handleUploadClick} />
  )
}

AgreementStatusCellWrapper.propTypes = {
  data: PropTypes.shape({}).isRequired,
  handleUploadClick: PropTypes.func.isRequired,
}

function InitialInvestmentHeaderComponent(props) {
  return (
    <TableHeader {...props}>
      <Tooltip
        content={TAXABLE_ACCOUNTS_INITIAL_INVESTMENT_TOOLTIP_CONTENT}
        ariaLabel="Initial investment information"
        plugins={[hideOnEsc, hideOnPopperBlur, preventEventBubbling]}
      >
        <InfoIcon className="icon-info" />
      </Tooltip>
    </TableHeader>
  )
}

function TaxableAccountsAgreementsTable({
  servicesAgreements,
  handleUploadClick,
  tableId,
  currentUser,
  filters,
  noRowsFoundMessage,
  noServicesAgreements,
  fieldMappings,
}) {
  const history = useHistory()
  const searchParams = useSearchParams()
  const [showFilterModal, setShowFilterModal] = useState(false)
  const hasFiltersApplied = !isEqual(filters, INITIAL_FILTERS)

  const searchValue = searchParams.q ?? ''

  const updateSearchParams = (filters, searchValue) => {
    history.push({
      pathname: location.pathname,
      search: convertToSearchParams({
        ...filters,
        q: searchValue,
      }),
    })
  }

  const isPlanSponsor =
    currentUser.accountAudience === AccountAudience.PLAN_FIDUCIARY
  const disabled = !servicesAgreements?.length
  const showIcon = disabled

  return (
    <>
      <TableHeading title={PlanAgreementsTableHeaders.TAXABLE_ACCOUNTS}>
        {!noServicesAgreements && (
          <DownloadTableButton
            data={servicesAgreements}
            fieldMappings={fieldMappings}
            fileName="Inspira Financial_Retirement Services_Taxable_Accounts Services Agreements"
            disabled={disabled}
            showIcon={showIcon}
          />
        )}
        <div className="input-container">
          <SearchInput
            type="search"
            aria-label="Search custodial agreements"
            placeholder={
              isPlanSponsor
                ? 'Search plan name'
                : 'Search plan sponsor, plan name, or EIN'
            }
            onChange={(e) => updateSearchParams(filters, e.target.value)}
            value={searchValue}
          />
          <Filter
            toggleMenu={() => setShowFilterModal((s) => !s)}
            isActive={hasFiltersApplied}
          />
          {showFilterModal && (
            <PlanAgreementsFilterDialog
              filters={filters}
              handleSubmit={(filterValues) =>
                updateSearchParams(filterValues, searchValue)
              }
              clearAllFilters={() =>
                updateSearchParams(INITIAL_FILTERS, searchValue)
              }
              handleClose={() => setShowFilterModal(false)}
              servicesAgreements={servicesAgreements}
              agreementsType={ProductAgreementType.TAXABLE_ACCOUNTS}
            />
          )}
        </div>
      </TableHeading>
      <PaginatedAndSortableTable
        data={servicesAgreements}
        initialAscending={false}
        initialColumn="createdDate"
        parentScrollContainerSelector="#main-content"
        className="custodial-agreements-table"
        noRowsFoundMessage={noRowsFoundMessage}
        tableId={tableId}
      >
        {!isPlanSponsor && (
          <TableColumn
            label="Plan sponsor name"
            name={'planSponsorCompany.name'}
            headerComponent={TableHeader}
            format={displayAttribute}
          />
        )}
        {!isPlanSponsor && (
          <TableColumn
            label="EIN"
            name={'planEIN'}
            format={displayEin}
            sortFunc={einSorter}
            headerComponent={TableHeader}
          />
        )}
        <TableColumn
          label="Complete legal plan name"
          name="name"
          component={PlanAgreementDetailsLink}
          headerComponent={TableHeader}
        />
        <TableColumn
          label="Initial investment"
          name="portalAgreementType"
          headerComponent={InitialInvestmentHeaderComponent}
          format={displayAttribute}
        />

        <TableColumn
          label="Agreement status"
          name="portalAgreementStatus"
          headerComponent={TableHeader}
          format={displayAttribute}
          component={AgreementStatusCellWrapper}
          handleUploadClick={handleUploadClick}
        />
        <TableColumn
          label="Date created"
          name="createdDate"
          format={(val) => formatDate(val)}
          sortFunc={dateSorter}
          headerComponent={TableHeader}
        />
      </PaginatedAndSortableTable>
    </>
  )
}

TaxableAccountsAgreementsTable.propTypes = propTypes
TaxableAccountsAgreementsTable.defaultProps = defaultProps
export default TaxableAccountsAgreementsTable
